//TODO: Currently designs not finalized for this, so we are not using this component in the project, remove or use post design finalization
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ButtonStack,
  CountButton,
  CounterRow,
  ErrorMessage,
  InputSection,
  NumberInput,
  SubTitle,
  TravellerInputContainer,
  TravellerInputWrapper,
  TravellerLabel,
} from "./TravellerCountInput.style";

export interface TravellerCountInputProps {
  /** Current value */
  value: number;
  /** Callback when value changes (only called with valid values) */
  onChange: (value: number) => void;
  /** Minimum allowed value (default: 1) */
  min: number;
  /** Maximum allowed value (default: 30) */
  max: number;
  /** Show label text next to input (e.g., "Traveller" or "Travellers") */
  showLabel?: boolean;
  /** Custom singular label text */
  singularLabel?: string;
  /** Custom plural label text */
  pluralLabel?: string;
  /** Placeholder text when input is empty */
  placeholder?: string;
  /** Disable the component */
  disabled?: boolean;
  /** Custom error message for value below min */
  minErrorMessage?: string;
  /** Custom error message for value above max */
  maxErrorMessage?: string;
  /** Show +/- buttons alongside input */
  showButtons?: boolean;
  /** Custom sub title for the input */
  subTitle?: string;
}

const TravellerCountInput: React.FC<TravellerCountInputProps> = ({
  value,
  onChange,
  min,
  max,
  showLabel = true,
  singularLabel = "Traveller",
  pluralLabel = "Travellers",
  placeholder = "00",
  disabled = false,
  minErrorMessage,
  maxErrorMessage,
  showButtons = true,
  subTitle,
}) => {
  const [inputValue, setInputValue] = useState<string>(value?.toString() || "");
  const [error, setError] = useState<string>("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync input value when prop value changes externally
  useEffect(() => {
    if (!hasInteracted) {
      setInputValue(value?.toString() || "");
    }
  }, [value, hasInteracted]);

  // Reset hasInteracted when input loses focus with valid value
  const handleBlur = useCallback(() => {
    const numValue = parseInt(inputValue, 10);

    if (inputValue === "" || isNaN(numValue)) {
      // Reset to current valid value if empty or invalid
      setInputValue(value?.toString() || min.toString());
      setError("");
      setHasInteracted(false);
      return;
    }

    if (numValue < min) {
      setError(
        minErrorMessage ||
          `Minimum ${min} traveller${min > 1 ? "s" : ""} required`
      );
      return;
    }

    if (numValue > max) {
      setError(maxErrorMessage || `Maximum ${max} travellers allowed`);
      return;
    }

    // Valid value - update parent
    setError("");
    setHasInteracted(false);
    if (numValue !== value) {
      onChange(numValue);
    }
  }, [inputValue, value, min, max, onChange, minErrorMessage, maxErrorMessage]);

  const validateAndSetError = useCallback(
    (numValue: number) => {
      if (numValue < min) {
        setError(
          minErrorMessage ||
            `Minimum ${min} traveller${min > 1 ? "s" : ""} required`
        );
        return false;
      }
      if (numValue > max) {
        setError(maxErrorMessage || `Maximum ${max} travellers allowed`);
        return false;
      }
      setError("");
      return true;
    },
    [min, max, minErrorMessage, maxErrorMessage]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      // Allow empty input
      if (rawValue === "") {
        setInputValue("");
        setError("");
        setHasInteracted(true);
        return;
      }

      // Only allow numeric input
      if (!/^\d*$/.test(rawValue)) {
        return;
      }

      setInputValue(rawValue);
      setHasInteracted(true);

      const numValue = parseInt(rawValue, 10);
      if (!isNaN(numValue)) {
        const isValid = validateAndSetError(numValue);
        if (isValid) {
          onChange(numValue);
        }
      }
    },
    [onChange, validateAndSetError]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleBlur();
        inputRef.current?.blur();
      }
      // Allow: backspace, delete, tab, escape, enter, decimal point
      if (
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "Tab" ||
        e.key === "Escape" ||
        e.key === "Enter" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "Home" ||
        e.key === "End"
      ) {
        return;
      }
      // Block non-numeric keys
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    },
    [handleBlur]
  );

  const handleIncrement = useCallback(() => {
    if (disabled) return;

    const currentValue = parseInt(inputValue, 10) || value || min;
    const newValue = currentValue + 1;

    if (newValue <= max) {
      setInputValue(newValue.toString());
      const isValid = validateAndSetError(newValue);
      if (isValid) {
        onChange(newValue);
      }
    }
  }, [inputValue, value, min, max, disabled, onChange, validateAndSetError]);

  const handleDecrement = useCallback(() => {
    if (disabled) return;

    const currentValue = parseInt(inputValue, 10) || value || min;
    const newValue = currentValue - 1;

    if (newValue >= min) {
      setInputValue(newValue.toString());
      const isValid = validateAndSetError(newValue);
      if (isValid) {
        onChange(newValue);
      }
    }
  }, [inputValue, value, min, disabled, onChange, validateAndSetError]);

  const currentNumValue = parseInt(inputValue, 10) || value || min;
  const isMinDisabled = disabled || currentNumValue <= min;
  const isMaxDisabled = disabled || currentNumValue >= max;
  const labelText = currentNumValue === 1 ? singularLabel : pluralLabel;

  return (
    <TravellerInputWrapper>
      <TravellerInputContainer hasError={!!error}>
        <CounterRow>
          <InputSection>
            <NumberInput
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              hasError={!!error}
              aria-label="Number of travellers"
              aria-invalid={!!error}
              aria-describedby={error ? "traveller-count-error" : undefined}
            />
            {showLabel && <TravellerLabel>{labelText}</TravellerLabel>}
          </InputSection>

          {showButtons && (
            <ButtonStack>
              <CountButton
                onClick={handleIncrement}
                disabled={isMaxDisabled}
                aria-label="Increase traveller count"
                type="button"
              >
                +
              </CountButton>
              <CountButton
                onClick={handleDecrement}
                disabled={isMinDisabled}
                aria-label="Decrease traveller count"
                type="button"
              >
                −
              </CountButton>
            </ButtonStack>
          )}
        </CounterRow>

        {subTitle && <SubTitle>{subTitle}</SubTitle>}
      </TravellerInputContainer>

      {error && (
        <ErrorMessage id="traveller-count-error" role="alert">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 0C3.136 0 0 3.136 0 7C0 10.864 3.136 14 7 14C10.864 14 14 10.864 14 7C14 3.136 10.864 0 7 0ZM7.7 10.5H6.3V9.1H7.7V10.5ZM7.7 7.7H6.3V3.5H7.7V7.7Z"
              fill="#E53E3E"
            />
          </svg>
          {error}
        </ErrorMessage>
      )}
    </TravellerInputWrapper>
  );
};

export default TravellerCountInput;
